import { Button, Dialog, notify, Select, StatusPill, TextField, Toggle, useConfirm, type PillTone } from "@flexfreight/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
export { ZohoBooksCard, ZohoConnectionCard, ZohoDeskCard, type ZohoView } from "./ZohoCards";

const when = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");


type MailboxRole = "ops" | "sales" | "marketing";
type M365View = {
  settings: { tenantId: string; clientId: string; mailboxes: string[]; access: Record<string, MailboxRole[]>; supportMailbox: string | null };
  secretSaved: boolean;
  configured: boolean;
  enabled: boolean;
  domains: string[];
  roles: MailboxRole[];
  lastTest: { at: string; ok: boolean | null; message: string | null } | null;
  sending: { enabled: boolean; on: boolean; label: string; changedAt: string | null; supportMailbox: string | null };
  status: "Awaiting Microsoft 365 credentials" | "Needs a passing test" | "Ready to turn on" | "Connected";
};
const M365_TONE: Record<M365View["status"], PillTone> = { "Awaiting Microsoft 365 credentials": "neutral", "Needs a passing test": "warning", "Ready to turn on": "info", Connected: "success" };
const M365_KEY = ["console", "integrations", "m365"];
const ROLE_LABEL: Record<MailboxRole, string> = { ops: "Operations", sales: "Sales", marketing: "Marketing" };
const accessText = (v: M365View, m: string) => {
  const roles = v.settings.access[m];
  return roles === undefined ? "everyone with email" : roles.length ? `owners, admins, ${roles.map((r) => ROLE_LABEL[r]).join(", ")}` : "owners and admins only";
};

/** Microsoft 365 mail (A.23/A.24): FlexFreight mailboxes only, access by role, outbound behind an owner-approved switch. */
export function M365Card({ canManage }: { canManage: boolean }) {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: M365_KEY, queryFn: () => api<M365View>("/v1/console/integrations/m365") });
  const [editing, setEditing] = useState(false);
  const { confirm, dialog } = useConfirm();
  const set = (v: M365View) => {
    qc.setQueryData(M365_KEY, v);
    void qc.invalidateQueries({ queryKey: ["console", "mailboxes"] });
  };
  const test = useMutation({
    mutationFn: () => api<M365View>("/v1/console/integrations/m365/test", { method: "POST" }),
    onSuccess: (v) => {
      set(v);
      if (v.lastTest?.ok) notify.success("Microsoft 365 connected", v.lastTest.message ?? undefined);
      else notify.error("Test didn't pass", v.lastTest?.message ?? undefined);
    },
    onError: (e) => notify.error("Couldn't test", message(e)),
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api<M365View>("/v1/console/integrations/m365", { method: "PATCH", body: { enabled } }),
    onSuccess: set,
    onError: (e) => notify.error("Couldn't change mail", message(e)),
  });
  const sendSwitch = useMutation({
    mutationFn: (enabled: boolean) => api<M365View>("/v1/console/integrations/m365/sending", { method: "PUT", body: { enabled } }),
    onSuccess: (v) => {
      set(v);
      notify.success(v.sending.enabled ? "Outbound email is on" : "Outbound email is off", v.sending.label);
    },
    onError: (e) => notify.error("Couldn't change sending", message(e)),
  });
  async function flipSending(on: boolean) {
    if (on && !(await confirm({ title: "Turn on outbound email?", body: "Staff with email access can then send from the FlexFreight mailboxes, and ticket replies are emailed from the support mailbox. Every send still asks for confirmation and is audited.", confirmLabel: "Turn on sending" }))) return;
    sendSwitch.mutate(on);
  }
  const v = q.data;
  const isOwner = me.role === "owner";
  return (
    <section className="ffx-card ffa-card ffa-integration" aria-labelledby="m365-h">
      <div className="ffa-card__headrow">
        <h2 id="m365-h" className="ffa-card__title">Microsoft 365 mail</h2>
        {v ? <StatusPill tone={M365_TONE[v.status]}>{v.status}</StatusPill> : null}
      </div>
      <p className="ffa-small ffa-muted">
        Shared FlexFreight mailboxes in the Inbox, read live (nothing inbound is stored). Only mailboxes on {v ? v.domains.map((d) => `@${d}`).join(", ") : "FlexFreight's domain"} can be added, so Empire mail
        can't be connected. Drafts are saved in FlexFreight; outbound email stays off until an owner approves it.
      </p>
      {v ? (
        <>
          <dl className="ffa-dl">
            <dt>Tenant</dt>
            <dd className="ffx-num">{v.settings.tenantId || "Not set"}</dd>
            <dt>App (client) ID</dt>
            <dd className="ffx-num">{v.settings.clientId || "Not set"}</dd>
            <dt>Client secret</dt>
            <dd>{v.secretSaved ? "Saved" : "Missing"}</dd>
            <dt>Mailboxes</dt>
            <dd>
              {v.settings.mailboxes.length ? (
                <ul className="ffa-plainlist">
                  {v.settings.mailboxes.map((m) => (
                    <li key={m}>
                      {m} <span className="ffa-muted">· {accessText(v, m)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                "None"
              )}
            </dd>
            <dt>Ticket replies from</dt>
            <dd>{v.sending.supportMailbox ?? "No support mailbox connected"}</dd>
            <dt>Last test</dt>
            <dd>{v.lastTest ? `${when.format(new Date(v.lastTest.at))}: ${v.lastTest.message ?? ""}` : "Never"}</dd>
            <dt>Sending</dt>
            <dd>
              <StatusPill tone={v.sending.on ? "success" : "warning"}>{v.sending.label}</StatusPill>
            </dd>
          </dl>
          {canManage ? (
            <div className="ffa-integration__actions">
              <Button onClick={() => setEditing(true)}>{v.configured ? "Change settings" : "Connect Microsoft 365"}</Button>
              <Button disabled={!v.configured} loading={test.isPending} onClick={() => test.mutate()}>Test connection</Button>
              <Toggle look="switch" label="Mail on" checked={v.enabled} disabled={!v.enabled && !v.lastTest?.ok} onChange={(on) => toggle.mutate(on)} />
              <Toggle
                look="switch"
                label="Outbound email"
                description={v.sending.enabled ? "On. Turning it off stops all sending at once." : isOwner ? "Owner approval: needs Mail.Send in Entra first." : "Only an owner can turn this on."}
                checked={v.sending.enabled}
                disabled={sendSwitch.isPending || (!v.sending.enabled && (!isOwner || !v.enabled || !v.configured))}
                onChange={(on) => void flipSending(on)}
              />
            </div>
          ) : null}
        </>
      ) : (
        <p className="ffa-muted">Loading...</p>
      )}
      {editing && v ? <M365Dialog v={v} onClose={() => setEditing(false)} onSaved={set} /> : null}
      {dialog}
    </section>
  );
}

function M365Dialog({ v, onClose, onSaved }: { v: M365View; onClose: () => void; onSaved: (v: M365View) => void }) {
  const [tenantId, setTenant] = useState(v.settings.tenantId);
  const [clientId, setClient] = useState(v.settings.clientId);
  const [secret, setSecret] = useState("");
  const [boxes, setBoxes] = useState(v.settings.mailboxes.join(", "));
  const [access, setAccess] = useState<Record<string, MailboxRole[]>>(v.settings.access);
  const [support, setSupport] = useState<string>(v.settings.supportMailbox ?? "auto");
  const list = [...new Set(boxes.split(/[\s,]+/).filter(Boolean).map((b) => b.toLowerCase()))];
  const outside = list.filter((b) => !v.domains.some((d) => b.endsWith(`@${d}`)));
  const guid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const appChanged = v.configured && (tenantId !== v.settings.tenantId || clientId !== v.settings.clientId);
  const needSecret = !v.secretSaved || appChanged;
  const rolesFor = (m: string) => access[m] ?? v.roles;
  const flip = (m: string, r: MailboxRole, on: boolean) => setAccess((a) => ({ ...a, [m]: on ? [...new Set([...rolesFor(m), r])] : rolesFor(m).filter((x) => x !== r) }));
  const save = useMutation({
    mutationFn: () =>
      api<M365View>("/v1/console/integrations/m365", {
        method: "PUT",
        body: {
          tenantId: tenantId.trim(),
          clientId: clientId.trim(),
          mailboxes: list,
          access: Object.fromEntries(Object.entries(access).filter(([m]) => list.includes(m))),
          supportMailbox: support !== "auto" && list.includes(support) ? support : null,
          ...(secret.trim() ? { clientSecret: secret.trim() } : {}),
        },
      }),
    onSuccess: (r) => {
      onSaved(r);
      notify.success("Microsoft 365 settings saved", "Mail and outbound email are off until a new test passes and they're turned back on.");
      onClose();
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const valid = guid.test(tenantId.trim()) && guid.test(clientId.trim()) && list.length > 0 && outside.length === 0 && (!needSecret || secret.trim().length >= 10);
  const okBoxes = list.filter((b) => !outside.includes(b));
  return (
    <Dialog
      open
      onClose={onClose}
      title="Microsoft 365 connection"
      description="An Entra app registration with Mail.Read and Mail.Send (application), limited by an application access policy to FlexFreight mailboxes. See docs/console/M365.md."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="ffa-m365-form" disabled={!valid} loading={save.isPending}>Save</Button>
        </>
      }
    >
      <form id="ffa-m365-form" className="ffa-form" autoComplete="off" onSubmit={(e) => { e.preventDefault(); if (valid) save.mutate(); }}>
        <TextField label="Directory (tenant) ID" value={tenantId} onChange={(e) => setTenant(e.target.value)} spellCheck={false} />
        <TextField label="Application (client) ID" value={clientId} onChange={(e) => setClient(e.target.value)} spellCheck={false} />
        <TextField label="Client secret" type="password" autoComplete="new-password" spellCheck={false} optional={!needSecret} hint={v.secretSaved && !needSecret ? "Saved. Leave blank to keep it." : "The secret's Value, not its ID"} value={secret} onChange={(e) => setSecret(e.target.value)} />
        <TextField label="FlexFreight mailboxes" value={boxes} onChange={(e) => setBoxes(e.target.value)} spellCheck={false} hint={`Comma separated, on ${v.domains.map((d) => `@${d}`).join(", ")} only (sales@, support@, ops@ ...)`} error={outside.length ? `Not a FlexFreight mailbox: ${outside.join(", ")}` : undefined} />
        {okBoxes.length ? (
          <fieldset className="ffa-access">
            <legend className="ffa-small"><strong>Who can use each mailbox</strong> (owners and admins always can)</legend>
            {okBoxes.map((m) => (
              <div key={m} className="ffa-access__row">
                <span className="ffa-access__box">{m}</span>
                <div className="ffa-access__roles">
                  {v.roles.map((r) => (
                    <Toggle key={r} label={ROLE_LABEL[r]} checked={rolesFor(m).includes(r)} onChange={(on) => flip(m, r, on)} />
                  ))}
                </div>
              </div>
            ))}
          </fieldset>
        ) : null}
        <Select label="Ticket replies are emailed from" value={support} onChange={setSupport} options={[{ value: "auto", label: "The support@ mailbox (automatic)" }, ...okBoxes.map((m) => ({ value: m, label: m }))]} hint="Only while outbound email is on. Otherwise replies are saved and shown in the portal." />
      </form>
    </Dialog>
  );
}

/** Flex AI (A.36): the Anthropic API key, write-only. Without it Flex AI runs in demonstration mode. */
export function AnthropicCard({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["console", "ai"], queryFn: () => api<{ mode: "live" | "demonstration"; model: string | null }>("/v1/console/ai") });
  const [key, setKey] = useState("");
  const save = useMutation({
    mutationFn: (enabled: boolean) => api<{ enabled: boolean }>("/v1/console/integrations/anthropic", { method: "PUT", body: { ...(key.trim() ? { apiKey: key.trim() } : {}), enabled } }),
    onSuccess: (r) => {
      setKey("");
      void qc.invalidateQueries({ queryKey: ["console", "ai"] });
      notify.success(r.enabled ? "Flex AI is live" : "Flex AI is in demonstration mode");
    },
    onError: (e) => notify.error("Couldn't save", message(e)),
  });
  const live = q.data?.mode === "live";
  return (
    <section className="ffx-card ffa-card ffa-integration" aria-labelledby="ai-h">
      <div className="ffa-card__headrow">
        <h2 id="ai-h" className="ffa-card__title">Flex AI (Anthropic)</h2>
        <StatusPill tone={live ? "success" : "neutral"}>{live ? `Live · ${q.data?.model}` : "Awaiting credentials"}</StatusPill>
      </div>
      <p className="ffa-small ffa-muted">Read-only tools under each staff member&apos;s own permissions. No tool can send, book, price or change anything.</p>
      {canManage ? (
        <div className="ffa-inline">
          <TextField label="Anthropic API key" type="password" autoComplete="new-password" spellCheck={false} value={key} onChange={(e) => setKey(e.target.value)} hint={live ? "Saved. Enter a new key to replace it." : "Starts with sk-ant-"} />
          <Button variant="primary" disabled={!key.trim()} loading={save.isPending} onClick={() => save.mutate(true)}>Save key</Button>
          {live ? <Button onClick={() => save.mutate(false)}>Turn off</Button> : null}
        </div>
      ) : null}
    </section>
  );
}
